import React, { useState } from "react";
import classNames from "classnames";
import { GearSixIcon, PlusCircleIcon, XCircleIcon } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createConversation, getConversations } from "api/conversations";
import SidebarAssistantItem from "components/SidebarAssistantItem";
import SidebarAgentItem from "components/SidebarAgentItem";
import SidebarConversationItem from "components/SidebarConversationItem";
import { getAssistants } from "api/assistants";
import { getAgents } from "api/agents";
import { TOOLS } from "config/tools";
import IconButton from "components/IconButton";
import useOnEscape from "hooks/useOnEscape";

export default function Sidebar({
    visible,
    setVisible
}) {

    const [createChatPopupVisible, setCreateChatPopupVisible] = useState(false);

    useOnEscape(createChatPopupVisible, () => setCreateChatPopupVisible(false));

    const cache = useQueryClient();
    const navigate = useNavigate();

    const Classes = classNames({
        'fixed z-[var(--z-nav)] bg-verylight flex flex-col top-0 bottom-0 w-[80vw] max-w-[320px] transition-all duration-300 ease-in-out border-r border-black/6': true,
        'left-0': visible,
        'left-[-80vw] lg:left-0': !visible,
    })

    // const { data: { data: conversations } = {}, isLoading } = useQuery({
    //     queryKey: ['conversations'],
    //     queryFn: async () => await getConversations(),
    // });

    const { data: assistants, isLoading: isLoadingAssistants } = useQuery({
        queryKey: ['assistants'],
        queryFn: async () => await getAssistants({}),
    });

    const { data: agents, isLoading: isLoadingAgents } = useQuery({
        queryKey: ['agents'],
        queryFn: async () => await getAgents({}),
    });

    const { data: conversations } = useQuery({
        queryKey: ['conversations'],
        queryFn: async () => await getConversations({
            params: {
            }
        }),
    });

    const { mutate } = useMutation({
        mutationKey: ['createConversation'],
        mutationFn: createConversation,
        onSuccess: (data) => {
            console.log("Conversation created successfully:", data);
            setCreateChatPopupVisible(false);
            cache.invalidateQueries(['conversations']);

            return navigate(`/conversations/${data.data._id}`);
        },
        onError: (error) => {
            console.error("Error creating conversation:", error);
        }
    });

    // const createChatHandler = () => {
    //     setCreateChatPopupVisible(true);
    // }

    const onSubmitHandler = (formData) => {
        const name = formData.get('name');
        if (!name || name.trim() === '') return;
        // chiamata API per creare la conversazione
        mutate({ name });
    }

    // if (isLoading) return <div>Loading...</div>;

    return (
        <>
            <div className={`lg:hidden fixed z-[var(--z-nav-backdrop)] top-0 left-0 w-full h-full bg-white/30 transition-all duration-300 ease-in-out ${visible ? 'delay-100 opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setVisible(false)}></div>

            <div className={Classes}>
                <div className='w-full px-5 py-6 box-border'>
                    <Link to="/" className="uppercase font-bold text-xl text-center tracking-wider">OPIFICIO.AI</Link>
                </div>

                <div className="flex-1 pb-20 px-3 overflow-y-scroll overflow-x-visible">

                    <div className="pt-5">
                        <p className="text-muted px-2 uppercase pb-2">Strumenti</p>
                        {TOOLS.map((tool) => {
                            const Icon = tool.icon;
                            return (
                                <Link
                                    key={tool.id}
                                    to={tool.path}
                                    className="group flex gap-2 items-center w-full p-2 text-muted hover:text-black cursor-pointer hover:bg-light/30 rounded-sm"
                                >
                                    <div className={`p-1.5 ${tool.bgColor} rounded-md`}>
                                        <Icon size={20} className={tool.color} />
                                    </div>
                                    <span className="text-sm">{tool.name}</span>
                                </Link>
                            );
                        })}
                    </div>

                    <div className="pt-5">
                        <p className="text-muted px-2 uppercase pb-2">Assistenti</p>
                        {isLoadingAssistants ?
                            <p className="text-muted px-2">Caricamento...</p>
                            :
                            <>
                                {assistants?.map((assistant) => {
                                    return (
                                        <SidebarAssistantItem
                                            key={assistant._id}
                                            assistant={assistant}
                                        />
                                    )
                                })}
                                <Link to="/assistants/create" className="group flex gap-2 items-center w-full p-2 text-muted hover:text-black cursor-pointer hover:bg-light/30 rounded-sm">
                                    <PlusCircleIcon size={26} className='fill-black' />
                                    nuovo assistente
                                </Link>
                            </>
                        }
                    </div>

                    <div className="pt-5">
                        <p className="text-muted px-2 uppercase pb-2">Agenti</p>
                        {isLoadingAgents ?
                            <p className="text-muted px-2">Caricamento...</p>
                            :
                            <>
                                {agents?.filter(agent => !agent.parent).map((agent) => {
                                    const childAgents = agents.filter(a => a.parent === agent._id);
                                    return (
                                        <SidebarAgentItem
                                            key={agent._id}
                                            agent={agent}
                                            childAgents={childAgents}
                                        />
                                    )
                                })}

                                <Link to="/agents/create" className="group flex gap-2 items-center w-full p-2 text-muted hover:text-black cursor-pointer hover:bg-light/30 rounded-sm">
                                    <PlusCircleIcon size={26} className='fill-black' />
                                    nuovo agente
                                </Link>
                            </>
                        }
                    </div>

                    <div className="pt-10">
                        <p className="text-muted px-2 uppercase pb-2">Conversazioni</p>
                        {conversations?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((conversation) => (
                            <SidebarConversationItem
                                key={conversation._id}
                                conversation={conversation}
                            />
                        ))}
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 w-full bg-verylight border-t border-black/6">
                    <Link className="block flex items-center gap-2 px-5 py-5 hover:bg-light/30" to="/settings">
                        <GearSixIcon size={20} className='fill-black' />
                        Impostazioni
                    </Link>
                </div>
            </div>

            <div className={`fixed z-[var(--z-modal)] flex top-0 left-0 lg:left-[320px] right-0 bottom-0 items-center justify-center transition-all backdrop-blur-sm ${createChatPopupVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>

                <div className='absolute top-0 left-0 right-0 bottom-0 bg-white/20  transition-all duration-300 ease-in-out flex items-center justify-center' onClick={() => setCreateChatPopupVisible(false)}></div>


                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="create-chat-title"
                    className={`relative max-w-[90vw] w-full max-w-[450px] max-h-[87vh] bg-white border border-black/10 shadow-2xl rounded-3xl transition-all duration-300 ease-out-quint flex flex-col ${createChatPopupVisible ? 'translate-y-0' : 'translate-y-3'}`}
                >
                    <div className='w-full border-b border-black/6 px-6 py-4 flex justify-between items-center'>
                        <p id="create-chat-title" className='text-lg font-medium text-black'>Nuova conversazione</p>

                        <IconButton label="Chiudi" onClick={() => setCreateChatPopupVisible(false)}>
                            <XCircleIcon size={28} className='fill-black hover:fill-black/80 transition-all duration-300 ease-in-out' />
                        </IconButton>
                    </div>
                    <form action={onSubmitHandler}>

                        <div className='flex-1 w-full overflow-y-scroll pb-18'>
                            <div className='px-6'>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    placeholder="Titolo della conversazione"
                                    aria-label="Titolo della conversazione"
                                    className="block w-full bg-transparent text-black placeholder:text-muted focus:outline-none resize-none text-[16px] leading-[1.55] border-b border-black/10 focus:border-black/30 pb-3 mb-6 transition-colors"
                                />
                            </div>
                        </div>

                        <button className='absolute bottom-0 left-0 w-full bg-black text-white py-5 text-center font-medium cursor-pointer hover:bg-greydark rounded-b-3xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-inset' type="submit">
                            Conferma
                        </button>
                    </form>
                </div>
            </div>
        </>
    )
}